'use client';

import React from 'react';
import { ListTree } from 'lucide-react';
import { clsx } from 'clsx';

interface PositionsToggleProps {
  active: boolean;
  onToggle: (next: boolean) => void;
}

/**
 * Schaltet die Treffer-Positionen unter den Belegzeilen ein und aus.
 *
 * Sitzt in der Controls-Zeile neben „Auswählen" und übernimmt dessen
 * Button-Muster, damit die Leiste einheitlich bleibt. Wird nur gerendert,
 * wenn ein Suchbegriff aktiv ist — ohne Suche gibt es nichts einzublenden.
 */
export function PositionsToggle({ active, onToggle }: PositionsToggleProps) {
  return (
    <button
      type="button"
      onClick={() => onToggle(!active)}
      aria-pressed={active}
      className={clsx(
        'inline-flex items-center gap-2 h-9 px-3.5 rounded-lg border text-sm font-semibold transition-colors',
        active
          ? 'border-primary bg-orange-50 text-primary'
          : 'border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50',
      )}
    >
      <ListTree size={16} />
      {active ? 'Positionen ausblenden' : 'Positionen einblenden'}
    </button>
  );
}

interface PositionsViewInput {
  /** Steht `positionen` überhaupt in der URL? */
  urlHasParam: boolean;
  /** Wert des URL-Parameters (`positionen=1`). */
  urlValue: boolean;
  /** Gemerkte Wahl. null = nichts gemerkt oder noch nicht gelesen. */
  remembered: boolean | null;
  /** Ist gerade ein Suchbegriff aktiv? */
  hasSearch: boolean;
}

/**
 * Entscheidet, ob die Positionsansicht an ist — und ob die URL nachzuziehen ist.
 *
 * Die URL gewinnt immer gegen die gemerkte Wahl: ein geteilter Link muss beim
 * Empfänger dasselbe zeigen wie beim Absender. Der gemerkte Wunsch greift nur,
 * wenn die URL schweigt UND eine Suche läuft — ohne Suchbegriff gibt es nichts
 * einzublenden.
 *
 * Als reine Funktion, damit die Regel ohne Apollo, Router und gerenderte Liste
 * prüfbar bleibt.
 */
export function resolvePositionsView({
  urlHasParam,
  urlValue,
  remembered,
  hasSearch,
}: PositionsViewInput): { active: boolean; shouldWriteUrl: boolean } {
  if (!hasSearch) return { active: false, shouldWriteUrl: false };
  if (urlHasParam) return { active: urlValue, shouldWriteUrl: false };
  if (remembered === true) return { active: true, shouldWriteUrl: true };
  return { active: false, shouldWriteUrl: false };
}
